import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { 
  User, Phone, AlertTriangle, Pill, ChevronDown, ChevronUp,
  AlertCircle, Clock, Heart, X, Camera, CameraOff
} from 'lucide-react';
import { cn, formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const ClientQuickProfile = ({ participantId, className, variant = 'panel' }) => {
  const { api } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(variant === 'expanded');
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/participants/${participantId}/basic-profile`);
      setProfile(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You are not assigned to this participant');
      } else {
        setError('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  }, [api, participantId]);

  useEffect(() => {
    if (participantId) {
      fetchProfile();
    }
  }, [participantId, fetchProfile]);

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'severe':
        return 'bg-red-500 text-white';
      case 'moderate':
        return 'bg-amber-500 text-white';
      case 'mild':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-muted';
    }
  };

  if (loading) {
    return (
      <div className={cn("p-4", className)}>
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("p-4 text-muted-foreground text-sm", className)}>
        <AlertCircle className="h-4 w-4 inline mr-2" />
        {error}
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  // Compact inline view for shift cards
  if (variant === 'inline') {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="text-xs"
      >
        <User className="h-3 w-3 mr-1" />
        Quick Profile
      </Button>
    );
  }

  // Expanded view for dialog (no collapsible wrapper)
  if (variant === 'expanded') {
    return (
      <div className={cn("space-y-4", className)}>
        {/* Basic Info */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-lg">{profile.full_name}</h3>
            {profile.preferred_name && (
              <p className="text-sm text-muted-foreground">
                Preferred: {profile.preferred_name}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              DOB: {formatDate(profile.date_of_birth)} ({profile.age} years)
            </p>
            {profile.house_name && (
              <p className="text-sm text-muted-foreground">
                House: {profile.house_name}
              </p>
            )}
            {profile.medicare_number && (
              <p className="text-sm text-muted-foreground">
                Medicare: {profile.medicare_number}{profile.medicare_expiry ? ` (Exp: ${formatDate(profile.medicare_expiry)})` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Allergies - CRITICAL SAFETY INFO */}
        {profile.allergies && profile.allergies.length > 0 && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3">
            <h4 className="font-semibold text-red-700 dark:text-red-400 flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4" />
              Allergies
            </h4>
            <div className="space-y-2">
              {profile.allergies.map((allergy, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <Badge className={cn("shrink-0", getSeverityColor(allergy.severity))}>
                    {allergy.severity}
                  </Badge>
                  <div className="text-sm">
                    <span className="font-medium">{allergy.substance}</span>
                    <span className="text-muted-foreground"> - {allergy.reaction}</span>
                    {allergy.action_plan && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        Action: {allergy.action_plan}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Medications */}
        {profile.medications && profile.medications.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-lg p-3">
            <h4 className="font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2 mb-2">
              <Pill className="h-4 w-4" />
              Medications ({profile.medication_count})
            </h4>
            <div className="space-y-2">
              {profile.medications.map((med, idx) => (
                <div key={idx} className="text-sm border-b border-blue-100 dark:border-blue-900 pb-2 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{med.name}</span>
                    {med.is_prn && (
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        PRN
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground">
                    {med.dose} • {med.frequency}
                  </p>
                  {med.instructions && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {med.instructions}
                    </p>
                  )}
                  {med.is_prn && med.prn_rules && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      PRN Rules: {med.prn_rules}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No medications message */}
        {(!profile.medications || profile.medications.length === 0) && (
          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3 flex items-center gap-2">
            <Pill className="h-4 w-4" />
            No active medications on record
          </div>
        )}

        {/* Emergency Contacts */}
        {profile.emergency_contacts && profile.emergency_contacts.length > 0 && (
          <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
            <h4 className="font-semibold text-green-700 dark:text-green-400 flex items-center gap-2 mb-2">
              <Phone className="h-4 w-4" />
              Emergency Contacts
            </h4>
            <div className="space-y-2">
              {profile.emergency_contacts.map((contact, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium">{contact.name}</span>
                    <span className="text-muted-foreground"> ({contact.relationship})</span>
                    {contact.priority === 1 && (
                      <Badge variant="outline" className="ml-2 text-xs">Primary</Badge>
                    )}
                  </div>
                  <a 
                    href={`tel:${contact.phone}`} 
                    className="text-green-600 dark:text-green-400 hover:underline flex items-center gap-1"
                  >
                    <Phone className="h-3 w-3" />
                    {contact.phone}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photography Permissions */}
        <div className={cn(
          "rounded-lg p-3 border",
          profile.photo_consent 
            ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900" 
            : "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900"
        )}>
          <h4 className={cn(
            "font-semibold flex items-center gap-2 mb-1",
            profile.photo_consent 
              ? "text-green-700 dark:text-green-400" 
              : "text-red-700 dark:text-red-400"
          )}>
            {profile.photo_consent ? (
              <Camera className="h-4 w-4" />
            ) : (
              <CameraOff className="h-4 w-4" />
            )}
            Photography Permission
          </h4>
          <div className="flex items-center gap-2">
            <Badge 
              variant="outline" 
              className={cn(
                "text-xs",
                profile.photo_consent 
                  ? "bg-green-100 text-green-700 border-green-300" 
                  : "bg-red-100 text-red-700 border-red-300"
              )}
            >
              {profile.photo_consent ? "Consent Given" : "No Consent"}
            </Badge>
          </div>
          {profile.photo_consent ? (
            <p className="text-xs text-green-600 dark:text-green-400 mt-2">
              Photos may be taken for daily diary and records
            </p>
          ) : (
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">
              Do NOT take photos of this participant
            </p>
          )}
          {profile.photo_consent_notes && (
            <p className="text-xs text-muted-foreground mt-1 italic">
              Note: {profile.photo_consent_notes}
            </p>
          )}
        </div>

        {/* Read-only notice */}
        <p className="text-xs text-muted-foreground text-center border-t pt-2">
          Read-only • Essential care information only
        </p>
      </div>
    );
  }

  // Collapsible panel view (default)
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <CollapsibleTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full justify-between"
          size="sm"
        >
          <span className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Quick Profile: {profile.full_name}
            {(profile.has_critical_allergies || profile.allergies?.length > 0) && (
              <Badge variant="destructive" className="ml-2">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Allergies
              </Badge>
            )}
          </span>
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-2">
        <Card className="border-primary/20">
          <CardContent className="p-4 space-y-4">
            {/* Basic Info */}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-base">{profile.full_name}</h3>
                {profile.preferred_name && (
                  <p className="text-sm text-muted-foreground">
                    Preferred: {profile.preferred_name}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  DOB: {formatDate(profile.date_of_birth)} ({profile.age} years)
                </p>
                {profile.house_name && (
                  <p className="text-sm text-muted-foreground">
                    House: {profile.house_name}
                  </p>
                )}
              </div>
            </div>

            {/* Allergies - CRITICAL SAFETY INFO */}
            {profile.allergies && profile.allergies.length > 0 && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3">
                <h4 className="font-semibold text-red-700 dark:text-red-400 flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  Allergies
                </h4>
                <div className="space-y-2">
                  {profile.allergies.map((allergy, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Badge className={cn("shrink-0", getSeverityColor(allergy.severity))}>
                        {allergy.severity}
                      </Badge>
                      <div className="text-sm">
                        <span className="font-medium">{allergy.substance}</span>
                        <span className="text-muted-foreground"> - {allergy.reaction}</span>
                        {allergy.action_plan && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Action: {allergy.action_plan}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Medications */}
            {profile.medications && profile.medications.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-lg p-3">
                <h4 className="font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2 mb-2">
                  <Pill className="h-4 w-4" />
                  Medications ({profile.medication_count})
                </h4>
                <div className="space-y-2">
                  {profile.medications.map((med, idx) => (
                    <div key={idx} className="text-sm border-b border-blue-100 dark:border-blue-900 pb-2 last:border-0 last:pb-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{med.name}</span>
                        {med.is_prn && (
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                            PRN
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground">
                        {med.dose} • {med.frequency}
                      </p>
                      {med.instructions && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          {med.instructions}
                        </p>
                      )}
                      {med.is_prn && med.prn_rules && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          PRN Rules: {med.prn_rules}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No medications message */}
            {(!profile.medications || profile.medications.length === 0) && (
              <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3 flex items-center gap-2">
                <Pill className="h-4 w-4" />
                No active medications on record
              </div>
            )}

            {/* Emergency Contacts */}
            {profile.emergency_contacts && profile.emergency_contacts.length > 0 && (
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
                <h4 className="font-semibold text-green-700 dark:text-green-400 flex items-center gap-2 mb-2">
                  <Phone className="h-4 w-4" />
                  Emergency Contacts
                </h4>
                <div className="space-y-2">
                  {profile.emergency_contacts.map((contact, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div>
                        <span className="font-medium">{contact.name}</span>
                        <span className="text-muted-foreground"> ({contact.relationship})</span>
                        {contact.priority === 1 && (
                          <Badge variant="outline" className="ml-2 text-xs">Primary</Badge>
                        )}
                      </div>
                      <a 
                        href={`tel:${contact.phone}`} 
                        className="text-green-600 dark:text-green-400 hover:underline flex items-center gap-1"
                      >
                        <Phone className="h-3 w-3" />
                        {contact.phone}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photography Permissions */}
            <div className={cn(
              "rounded-lg p-3 border",
              profile.photo_consent 
                ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900" 
                : "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900"
            )}>
              <h4 className={cn(
                "font-semibold flex items-center gap-2 mb-1",
                profile.photo_consent 
                  ? "text-green-700 dark:text-green-400" 
                  : "text-red-700 dark:text-red-400"
              )}>
                {profile.photo_consent ? (
                  <Camera className="h-4 w-4" />
                ) : (
                  <CameraOff className="h-4 w-4" />
                )}
                Photography Permission
              </h4>
              <div className="flex items-center gap-2">
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-xs",
                    profile.photo_consent 
                      ? "bg-green-100 text-green-700 border-green-300" 
                      : "bg-red-100 text-red-700 border-red-300"
                  )}
                >
                  {profile.photo_consent ? "Consent Given" : "No Consent"}
                </Badge>
              </div>
              {profile.photo_consent ? (
                <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                  Photos may be taken for daily diary and records
                </p>
              ) : (
                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                  Do NOT take photos of this participant
                </p>
              )}
              {profile.photo_consent_notes && (
                <p className="text-xs text-muted-foreground mt-1 italic">
                  Note: {profile.photo_consent_notes}
                </p>
              )}
            </div>

            {/* Read-only notice */}
            <p className="text-xs text-muted-foreground text-center border-t pt-2">
              Read-only • Essential care information only
            </p>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ClientQuickProfile;
